import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Theme, ThemeDetails, Me } from '@yandex-int/k-common/typings';
import { getExperimentTestId, LessonInfo } from 'platform-components';
import { DEFAULT_PAGE_TO_LOAD } from 'platform-components/constants';
import { getMe } from 'platform-components/src/components/user/selectors';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import ym from 'react-yandex-metrika';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { mapLessonsToIds } from 'common.components/lesson/utils';
import withTime, { InjectedProps as WithTimeProps } from 'common.components/timer/withTime';
import * as userActions from 'common.components/user/actions';
import * as userSelectors from 'common.components/user/selectors';
import { prepLessonsActions } from 'store/prep-lessons/actions';
import { useLoadPrepLessonsDetails } from 'store/prep-lessons/hooks/use-load-prep-lessons-details';
import {
    getPrepLessonsLoadingStatus,
    getPrepLessonsDetails,
    getPrepLessonsDetailsLoadingStatus,
    getPrepLessonsWithDetails,
} from 'store/prep-lessons/selectors';
import { PrepLessonDetailsClientSide } from 'store/prep-lessons/typings';
import * as themesActions from 'store/themes/actions';
import * as themesSelectors from 'store/themes/selectors';
import { doesThemeExist as doesThemeExistUtil } from 'store/themes/utils';
import { scrollTo } from 'utils/scroll-to';

import { ReduxTeacherStore, RootAction } from '../../typings';
import * as storeActions from '../library.store/actions';
import { getCurrentShowcaseSlug, getCurrentSubjectPageSlug } from '../library.store/selectors';
import * as storeSelectors from '../library.store/selectors';

import LibraryPromoPageView from './promo.view';
import * as selectors from './selectors';
import { Filter } from './typings';

interface StoreProps {
    classId: number;
    subject: string;
    themeId: number;
    themeDetails: ThemeDetails | null;
    rootThemesLoadingStatus: LOADING_STATUSES;
    themesLoadingStatus: LOADING_STATUSES;
    themeDetailsLoadingStatus: LOADING_STATUSES;
    prepLessonsLoadingStatus: LOADING_STATUSES;
    prepLessonsDetails: Record<number, PrepLessonDetailsClientSide>;
    prepLessonsDetailsLoadingStatus: LOADING_STATUSES;
    filterId: number;
    courseId: number | null;
    grade: number | null;

    me: Me | null;
    testId: string | null;
    currentShowcaseSlug: string | null;
    currentSubjectPageSlug: string | null;

    filters: Filter[];

    prepLessons: LessonInfo[];
    prepLessonsIds: number[];
    childThemes: Theme[];
    themes: Theme[];
}

interface DispatchProps {
    getRootThemesRequest: typeof themesActions.getRootThemesRequest;
    getThemeDetailsRequest: typeof storeActions.getThemeDetailsRequest;
    getThemesTreeRequest: typeof themesActions.getThemesTreeRequest;
    loadPrepLessonsRequest: typeof prepLessonsActions.loadPrepLessonsRequest;
    loadPrepLessonsDetailsRequest: typeof prepLessonsActions.loadPrepLessonsDetailsRequest;
    loadLessonViewData: typeof lessonViewActions.loadLessonViewData;
    toggleLessonModal: typeof lessonViewActions.toggleModal;
    setWizardVariables: typeof userActions.setWizardVariables;
}

interface Props extends WithRouterPathProps, WithTimeProps, StoreProps, DispatchProps {}

interface Context {
    initialRender: boolean;
    staticUrls: {
        labLibrarySubject: string;
        deepLabLibrary: string;
    };
}

const LibraryPromoPage = (props: Props, context: Context) => {
    const {
        classId,
        getRouterPath,
        subject,
        childThemes,
        prepLessons,
        prepLessonsIds,
        prepLessonsLoadingStatus,
        prepLessonsDetails,
        prepLessonsDetailsLoadingStatus,
        filters,
        rootThemesLoadingStatus,
        themeDetails,
        themeDetailsLoadingStatus,
        themesLoadingStatus,
        themeId,
        themes,
        filterId,
        courseId,
        now,
        grade,
        location: { search },
        match: { params },
        history,

        me,
        testId,
        currentShowcaseSlug,
        currentSubjectPageSlug,

        loadLessonViewData,
        toggleLessonModal,
        setWizardVariables,

        getRootThemesRequest,
        getThemeDetailsRequest,
        getThemesTreeRequest,

        loadPrepLessonsRequest,
        loadPrepLessonsDetailsRequest,
    } = props;

    const { initialRender, staticUrls } = context;

    const handleSearchParameters = useCallback(
        (searchArg: string) => {
            const { preparedLessonId, ...restParams } = qs.parse(searchArg);

            if (preparedLessonId && themeId) {
                loadLessonViewData({
                    lessonId: Number(preparedLessonId),
                    courseId,
                });
                toggleLessonModal(true);
                setWizardVariables({ deepLinkPreparedLesson: true });

                history.replace({
                    search: qs.stringify(restParams, { arrayFormat: 'bracket' }),
                });

                ym('reachGoal', 'teacher_lesson_creation_readylesson_visit', {
                    teacher_id: me?.id,
                    team_id: classId,
                    subject,
                    lesson_id: Number(preparedLessonId),
                    theme_id: themeId,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [themeId]
    );

    useEffect(() => {
        handleSearchParameters(search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (rootThemesLoadingStatus === LOADING_STATUSES.UNSENT) {
            getRootThemesRequest(subject);
        }
    }, [getRootThemesRequest, initialRender, subject, rootThemesLoadingStatus]);

    useEffect(() => {
        const shouldLoadThemeDetails =
            themeId && !themeDetails && !initialRender && themeDetailsLoadingStatus !== LOADING_STATUSES.LOADING;

        if (shouldLoadThemeDetails) {
            getThemeDetailsRequest(themeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeId]);

    useEffect(() => {
        if (themeId && filterId && (!filters || !filters.length) && !initialRender) {
            getThemesTreeRequest(subject, themeId, 2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeId, filterId]);

    useEffect(() => {
        if (themeId && !initialRender) {
            loadPrepLessonsRequest({ themeId, page: DEFAULT_PAGE_TO_LOAD, pageSize: 1000 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeId]);

    useLoadPrepLessonsDetails({
        courseId,
        prepLessonsIds,
        prepLessonsDetails,
        prepLessonsDetailsLoadingStatus,
        loadPrepLessonsDetailsRequest,
    });

    const metrikaParams = useMemo(
        () => ({
            teacher_id: me?.id || null,
            subject_slug: subject,
            team_level: grade,
            theme_id: filterId,
            test_id: testId,
            general_showcase_slug: currentShowcaseSlug,
            subject_page_showcase_slug: currentSubjectPageSlug,
        }),
        [me?.id, subject, grade, filterId, testId, currentShowcaseSlug, currentSubjectPageSlug]
    );

    const doesThemeExist = useMemo(() => doesThemeExistUtil(themeId, themes), [themeId, themes]);

    if (
        themesLoadingStatus !== LOADING_STATUSES.LOADING &&
        themesLoadingStatus !== LOADING_STATUSES.UNSENT &&
        !doesThemeExist
    ) {
        const path = params.classId ? staticUrls.labLibrarySubject : staticUrls.deepLabLibrary;
        return <Redirect to={getRouterPath(path)} />;
    }

    return (
        <LibraryPromoPageView
            filterId={filterId}
            filters={filters}
            isLoading={prepLessonsLoadingStatus === LOADING_STATUSES.LOADING}
            metrikaParams={metrikaParams}
            now={now}
            prepLessons={prepLessons}
            themeDetails={themeDetails}
            themeId={themeId}
            themes={childThemes}
        />
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: WithRouterPathProps) => {
    const {
        match: {
            params: { classId: cId, themeId: thId, sectionId, subject },
        },
    } = ownProps;
    const themeId = Number(thId);
    const filterId = Number(sectionId);
    const classId = Number(cId);
    const grade = userSelectors.getCurrentTeamLevel(storeState, { classId });
    const courseId = getCurrentYearCourseId(storeState);
    const prepLessons = getPrepLessonsWithDetails(storeState, {
        themeId,
        courseId,
    });
    const prepLessonsIds = mapLessonsToIds(prepLessons);

    return {
        classId,
        subject,
        themeId,
        filterId,
        prepLessons,
        prepLessonsIds,
        grade,
        me: getMe(storeState),
        courseId,
        themeDetails: storeSelectors.getThemeDetails(storeState, { themeId }),
        themeDetailsLoadingStatus: storeSelectors.getThemeDetailsLoadingStatus(storeState, { themeId }),
        filters: selectors.getFilters(storeState, { themeId, subject, grade }),
        prepLessonsLoadingStatus: getPrepLessonsLoadingStatus(storeState, { themeId }),
        prepLessonsDetails: getPrepLessonsDetails(storeState, { courseId }),
        prepLessonsDetailsLoadingStatus: getPrepLessonsDetailsLoadingStatus(storeState, {
            courseId,
            lessonsIds: prepLessonsIds,
        }),
        childThemes: themesSelectors.getChildThemes(storeState, { themeId: filterId, subject, grade }),
        themes: themesSelectors.getThemes(storeState, { subject, grade }),
        themesLoadingStatus: themesSelectors.getThemesTreeLoadingStatus(storeState, { subject, themeId, grade }),
        rootThemesLoadingStatus: themesSelectors.getRootThemesLoadingStatus(storeState, { subject }),

        testId: getExperimentTestId(storeState),
        currentShowcaseSlug: getCurrentShowcaseSlug(storeState),
        // @ts-ignore
        currentSubjectPageSlug: getCurrentSubjectPageSlug(storeState, { subject, classId, grade }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    const { loadPrepLessonsRequest, loadPrepLessonsDetailsRequest } = prepLessonsActions;
    const { loadLessonViewData, toggleModal: toggleLessonModal } = lessonViewActions;
    const { setWizardVariables } = userActions;
    const { getThemeDetailsRequest } = storeActions;
    const { getRootThemesRequest, getThemesTreeRequest } = themesActions;
    return bindActionCreators(
        {
            loadLessonViewData,
            toggleLessonModal,
            setWizardVariables,
            getThemeDetailsRequest,
            getRootThemesRequest,
            getThemesTreeRequest,
            loadPrepLessonsRequest,
            loadPrepLessonsDetailsRequest,
        },
        dispatch
    );
};

LibraryPromoPage.contextTypes = {
    data: PropTypes.object,
    initialRender: PropTypes.bool.isRequired,
    staticUrls: PropTypes.object.isRequired,
};

export const LibraryPromoPageContainer = withRouterPath(
    withTime(connect(mapStateToProps, mapDispatchToProps)(LibraryPromoPage))
);
